import styled from '@emotion/styled';
import { uniqueId } from '@lessonup/utils';
import { rem } from 'polished';
import React, { InputHTMLAttributes, Ref, useCallback, useImperativeHandle, useRef } from 'react';
import { ComponentColors, ComponentSize, getComponentColors, spacing, spacingScaled } from '../../../foundations';
import { SpaceBetween } from '../../../utils/SpaceBetween/SpaceBetween';
import { Button } from '../../Button/Button';
import { IconChevronDown, IconChevronUp, IconEmail, IconLinkAdd, IconSearch } from '../../icons';
import { InputLabel } from '../InputLabel/InputLabel';
import { Validation } from '../Validation/Validation';
import { InputFieldOrientation, SharedInputComponentProps } from '../input.types';
import {
  getInputFieldBorderAndColorStyles,
  getInputFieldFontStyles,
  inputFieldPaddingSize,
  inputFieldPlaceholderOpacity,
  inputLineHeightMap,
} from '../input.utils';

/** This is a subset of native HTML input types that are supported by this component */
export type TextFieldType = 'text' | 'number' | 'search' | 'password' | 'email' | 'url';

/**
 * Props derived from the native HTML input element, excluding conflicting attributes like 'size', 'width', and 'type',
 * which are redefined by the component.
 */
export type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'width' | 'type' | 'disabled'>;

export interface TextFieldProps extends NativeInputProps, SharedInputComponentProps {
  ref?: Ref<HTMLInputElement>;
  type?: TextFieldType;

  /** Slot inside the field, left of the input. Meant for icons */
  iconSlot?: React.ReactNode;

  /** Slot that falls outside of the field */
  endSlot?: React.ReactNode;

  /** Whether the label is displayed above or to the left of the input */
  orientation?: InputFieldOrientation;
}

const iconSlotMap: Record<TextFieldType, React.ReactNode> = {
  text: null,
  number: null,
  password: null,
  search: <IconSearch />,
  email: <IconEmail />,
  url: <IconLinkAdd />,
};

export const TextField: React.FC<TextFieldProps> = (props) => {
  const {
    ref,
    type = 'text',
    id = uniqueId(`text-field-${type}-`),
    colorMode = 'neutral',
    size = 'small',
    className,
    validation,
    disabled,
    width,
    label,
    placeholder,
    iconSlot,
    endSlot,
    orientation = 'vertical',
    // Remaining props to be passed to the native input element
    ...nativeInputProps
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const colors = getComponentColors(colorMode, { disabled, noGradient: true });
  const appliedIconSlot = iconSlot || iconSlotMap[type];

  const validationId = `${id}-validation`;
  const describedById = validation?.message ? validationId : undefined;

  const handleFocus = useCallback(() => {
    if (!disabled && document.activeElement !== inputRef.current) {
      inputRef.current?.focus();
    }
  }, [inputRef, disabled]);

  const handleNumberIncrement = useCallback(
    (stepDirection: 'up' | 'down') => {
      if (!inputRef.current) return;
      stepDirection === 'up' ? inputRef.current.stepUp() : inputRef.current.stepDown();

      // Trigger input event to notify the change
      const event = new Event('input', { bubbles: true });
      inputRef.current.dispatchEvent(event);
    },
    [inputRef]
  );

  return (
    <StyledTextFieldWrapper size={size} direction="y" spacing={spacing.size4} $width={width} className={className}>
      <SpaceBetween
        direction={orientation === 'horizontal' ? 'x' : 'y'}
        alignItems={orientation === 'horizontal' ? 'center' : undefined}
        spacing={orientation === 'horizontal' ? spacing.size8 : spacing.size2}
        size={size}
      >
        <InputLabel htmlFor={props.id} size={size}>
          {label}
        </InputLabel>
        <SpaceBetween direction="x" spacing={spacing.size4} size={size} alignItems="center">
          <StyledTextField
            direction="x"
            spacing={spacing.size2}
            colors={colors}
            padding={spacingScaled(inputFieldPaddingSize, size)}
            size={size}
            alignItems="center"
            onClick={handleFocus}
          >
            {appliedIconSlot && <StyledIconSlotWrapper size={size}>{appliedIconSlot}</StyledIconSlotWrapper>}
            <StyledInput
              {...nativeInputProps}
              type={type}
              ref={inputRef}
              id={id}
              disabled={disabled}
              colors={colors}
              $size={size}
              placeholder={placeholder}
              aria-label={props['aria-label'] || label}
              aria-describedby={describedById}
              aria-invalid={validation?.type === 'negative' && true}
            />
            {type === 'number' && (
              <SpaceBetween direction="y" spacing={spacing.size0}>
                <StyledSpinButton
                  type="button"
                  size={size}
                  buttonType={colorMode}
                  disabled={disabled}
                  buttonShape="rect"
                  iconStart={<IconChevronUp />}
                  onClick={() => handleNumberIncrement('up')}
                />
                <StyledSpinButton
                  type="button"
                  size={size}
                  buttonType={colorMode}
                  disabled={disabled}
                  buttonShape="rect"
                  iconStart={<IconChevronDown />}
                  onClick={() => handleNumberIncrement('down')}
                />
              </SpaceBetween>
            )}
          </StyledTextField>
          {endSlot}
        </SpaceBetween>
      </SpaceBetween>
      {validation && <Validation id={validationId} size={size} colorMode={colorMode} {...validation} />}
    </StyledTextFieldWrapper>
  );
};

const StyledTextFieldWrapper = styled(SpaceBetween)<{ $width?: number }>`
  width: ${({ $width }) => ($width ? rem($width) : 'auto')};
  position: relative;
`;

const StyledTextField = styled(SpaceBetween)<{ colors: ComponentColors; size: ComponentSize }>`
  ${({ colors, size }) => getInputFieldBorderAndColorStyles(colors, size)}
  flex-grow: 1;

  :hover {
    background-color: ${({ colors }) => colors.fillFeedback};
    cursor: text;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
`;

const StyledInput = styled.input<{ $size: ComponentSize; colors: ComponentColors }>`
  border: none;
  color: inherit;
  background-color: transparent;
  ${({ $size }) => getInputFieldFontStyles($size)};
  height: ${({ $size }) => inputLineHeightMap[$size]};
  padding-left: ${({ $size }) => `${rem(spacingScaled(spacing.size2, $size))}`};
  overflow: hidden;
  width: 100%;

  ::placeholder {
    color: inherit;
    opacity: ${inputFieldPlaceholderOpacity};
  }

  :focus {
    outline: none;
  }
`;

const StyledIconSlotWrapper = styled.div<{ size: ComponentSize }>`
  width: ${({ size }) => inputLineHeightMap[size]};
  height: ${({ size }) => inputLineHeightMap[size]};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;

  > *:first-child {
    width: 100%;
    height: 100%;
  }
`;

const StyledSpinButton = styled(Button)`
  width: ${rem(24)};
  height: ${rem(8)};

  > * {
    pointer-events: none; // Prevent the button from being clicked for overflowing content
  }
`;
